import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ExternalLink, Crown, Trophy, Handshake } from "lucide-react";
import SponsorLeadForm from "../components/SponsorLeadForm";

// Tier display config — larger grids / smaller column counts = bigger tiles.
const TIER_CONFIG = {
    platinum: {
        label: "Platinum Sponsors",
        icon: Crown,
        cols: "grid-cols-1 md:grid-cols-1 lg:grid-cols-2",
        aspect: "aspect-[16/9]",
        padding: "p-10 md:p-16",
        sectionGap: "mb-20",
    },
    gold: {
        label: "Gold Sponsors",
        icon: Trophy,
        cols: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
        aspect: "aspect-[4/3]",
        padding: "p-8 md:p-10",
        sectionGap: "mb-16",
    },
    partner: {
        label: "Partner Sponsors",
        icon: Handshake,
        cols: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
        aspect: "aspect-square",
        padding: "p-6",
        sectionGap: "mb-12",
    },
};

const TIER_ORDER = ["platinum", "gold", "partner"];

export default function Sponsors() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/sponsors").then((r) => setItems(r.data)); }, []);

    const grouped = TIER_ORDER
        .map((t) => ({ tier: t, items: items.filter((i) => i.tier === t) }))
        .filter((g) => g.items.length > 0);

    return (
        <div data-testid="sponsors-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">Royal Court</div>
                    <h1 className="h-display text-6xl md:text-8xl">Sponsors & Partners</h1>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
                {grouped.map((g) => {
                    const cfg = TIER_CONFIG[g.tier];
                    const Icon = cfg.icon;
                    return (
                        <div key={g.tier} data-testid={`tier-${g.tier}`} className={cfg.sectionGap}>
                            <div className="flex items-center gap-3 mb-6">
                                <Icon size={14} className="text-gold" />
                                <span className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold">
                                    {cfg.label}
                                </span>
                                <span className="flex-1 h-px bg-line" />
                            </div>
                            <div className={`grid ${cfg.cols} gap-4`}>
                                {g.items.map((s, i) => (
                                    <a
                                        key={s.id}
                                        href={s.website || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={`${cfg.aspect} grid place-items-center ${cfg.padding} group relative bg-white border border-line hover:border-gold transition-all overflow-hidden`}
                                        data-testid={`sponsor-${g.tier}-${i}`}
                                    >
                                        {s.logo_url ? (
                                            <img
                                                src={s.logo_url}
                                                alt={s.name}
                                                className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                                            />
                                        ) : (
                                            <span className="h-display text-lg text-bg text-center">{s.name}</span>
                                        )}
                                        <div className="absolute bottom-0 inset-x-0 px-3 py-2 bg-black/90 text-white flex items-center justify-between gap-2 translate-y-full group-hover:translate-y-0 transition-transform">
                                            <span className="text-[11px] tracking-widest uppercase font-bold truncate">{s.name}</span>
                                            <ExternalLink size={12} className="text-gold shrink-0" />
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && <div className="text-center text-ink py-20">No sponsors yet.</div>}
            </section>

            <section className="max-w-5xl mx-auto px-5 md:px-10 pb-24">
                <SponsorLeadForm />
            </section>
        </div>
    );
}
